import Tabs from '@/components/molecule/Tabs';
import Table from '@/components/atom/Table';
import React, { useCallback, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import {
  MultipleTriggerProps,
  SingleTriggerProps,
} from '@/components/molecule/Tabs/types';

interface Props {
  defaultTab: string;
  onTabChange: (e: string) => void;
  gridRef: React.RefObject<AgGridReact>;
  tabTriggers: (MultipleTriggerProps & SingleTriggerProps)[];
  rowData: any[] | undefined;
  isError: boolean;
  columnDefs: ColDef[];
  totalCount: number | undefined;
  page: number;
  pageSize: number;
  handlePageChange: (page: number, pageSize: number) => void;
  isPagenation?: boolean;
}

const MemoizedTable = React.memo(Table);

function TabTable({
  defaultTab,
  onTabChange,
  gridRef,
  tabTriggers,
  isError,
  rowData,
  columnDefs,
  totalCount,
  page,
  pageSize,
  handlePageChange,
  isPagenation = true,
}: Props) {
  const [tab, setTab] = useState(defaultTab);

  const handleTabChange = useCallback(
    (e: string) => {
      onTabChange?.(e);
      setTab(e);
    },
    [onTabChange],
  );

  const includeValues = useMemo(() => {
    const result: string[] = [];

    tabTriggers.forEach(item => {
      if (item?.optionTrigger) {
        item?.optionTrigger.forEach(innerItem => {
          result.push(innerItem.value);
        });
      } else {
        result.push(item.value);
      }
    });

    return result;
  }, [tabTriggers]);

  return (
    <div className="mt-[16px]">
      <Tabs
        onChange={handleTabChange}
        hasSearchParams={true}
        defaultValue={defaultTab}
        includeValues={includeValues}
      >
        <div className="mb-4">
          <Tabs.TabsList>
            {tabTriggers &&
              tabTriggers.map((e, idx) => {
                if (e?.optionTrigger) {
                  return (
                    <Tabs.MultipleTrigger
                      key={`trigger_${e.value}`}
                      value={e.value}
                      label={e.label}
                      optionTrigger={e.optionTrigger}
                    />
                  );
                }
                return (
                  <Tabs.SingleTrigger
                    defaultChecked={idx === 0}
                    key={`trigger_${e.value}`}
                    value={e.value}
                    label={e.label}
                  />
                );
              })}
          </Tabs.TabsList>
        </div>
      </Tabs>
      <MemoizedTable
        key={tab}
        ref={gridRef}
        resetRowDataOnUpdate={true}
        rowData={isError ? [] : rowData || []}
        isPaginationDataMapping
        columnDefs={columnDefs}
        total={totalCount || 0}
        page={page}
        pageSize={pageSize}
        isPagination={isPagenation}
        handlePaginationClick={handlePageChange}
      />
    </div>
  );
}

export default React.memo(TabTable);
